import {ClientProvider} from '@packages/client';
// Import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import {InvalidTokenError} from '@packages/utils';
import {QueryClientProvider} from '@tanstack/react-query';
import type {NextPage} from 'next';
import type {AppProps} from 'next/app';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import Script from 'next/script';
import {destroyCookie, parseCookies} from 'nookies';
import {type ReactElement, type ReactNode, useEffect} from 'react';
import {RecoilRoot} from 'recoil';
import {FAVICON_PATH, OGP_IMAGE_PATH_DEFAULT} from '../config/constants/img';
import {initDatadog} from '../lib/datadog';
import {queryClient} from '../lib/react-query';
import {initMocks} from '../mocks';
import {ClientSideErrorPage} from '../shared/components/Error/ClientSideErrorPage';
import {StudentVerificationRequiredPage} from '../shared/components/Error/StudentVerificationRequiredPage';
import {RegisterPrompts} from '../shared/components/RegisterPrompts';
import {RegistrationNavigationFloating} from '../shared/components/RegistrationNavigationFloating';
import '../styles/globals.css';
import {GSSKT_HOST} from '../config/constants/hosts';
import {AuthProvider} from '../core/usecases/auth/AuthProvider';
import {checkLightUserRequestFlagExistence} from '../core/usecases/auth/checkLightUserRequest';
import {checkRefreshTokenExistence} from '../core/usecases/auth/checkOauthTokenExistence';
import {destroyCookieWithLogout} from '../core/usecases/auth/destroyCookieWithLogout';
import {handleRefreshAccessToken} from '../core/usecases/auth/handleRefreshAccessToken';
import {sendOauthTokenToRNApp} from '../core/usecases/auth/sendOauthTokenToRNApp';
import {capturePromotionCampaignCode} from '../core/usecases/promotionCampaign/promotionCampaignCode';
import {cookieLightUserRequestKey} from '../server/token/cookieLightUserRequest';
import {
  cookieApiRefreshTokenKey,
  cookieApiTokenKey,
} from '../server/token/cookieOauthTokenKey';
import {SessionExpiredPage} from '../shared/components/Session/SessionExpiredPage';
import {SessionExpiredPopup} from '../shared/components/Session/SessionExpiredPopup';
import {SignUpAppealModalProvider} from '../shared/contexts/SignUpAppealModalContext';

// MEMO: APIモックを有効化する
// 有効にしない場合は、process.env.NEXT_PUBLIC_API_MOCKINGを空にする
// process.env.NEXT_PUBLIC_API_MOCKING === ""
if (
  process.env.NODE_ENV === 'development' &&
  process.env.NEXT_PUBLIC_API_MOCKING === 'enabled'
) {
  void initMocks();
}

initDatadog();

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<
  P,
  IP
> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const REFRESH_ACCESS_TOKEN_TIMER_ID = 'refreshAccessTokenTimerId';

const App = ({Component, pageProps}: AppPropsWithLayout): JSX.Element => {
  const router = useRouter();

  // 任意ページの ?pcid= 付き来訪でプロモーションキャンペーンコードを捕捉しCookieに保存する(last-touch)
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    capturePromotionCampaignCode(router.query);
  }, [router.isReady, router.query]);

  if (typeof window !== 'undefined') {
    const cookies = parseCookies();

    const {isValid: isValidRefreshToken} = checkRefreshTokenExistence(
      cookies[cookieApiRefreshTokenKey],
    );
    const {isValid: isLightUserRequest} = checkLightUserRequestFlagExistence(
      cookies[cookieLightUserRequestKey],
    );

    if (isLightUserRequest) {
      if (isValidRefreshToken) {
        // ライトユーザーの場合で前回ログイン時のCookieが残っている場合はCookieを削除してリロードする
        destroyCookieWithLogout();
        router.reload();
      }

      // 再ログイン時の影響を考慮して、ライトユーザー判別用のCookieを削除する
      destroyCookie(null, cookieLightUserRequestKey, {path: '/'});
    }

    // AccessTokenとrefreshTokenをアプリ側に送信する
    if (
      isValidRefreshToken &&
      (window as any).ReactNativeWebView &&
      !isLightUserRequest
    ) {
      sendOauthTokenToRNApp({
        accessToken: cookies[cookieApiTokenKey] ?? '',
        refreshToken: cookies[cookieApiRefreshTokenKey] ?? '',
      });
    }

    // 既存のAccessToken更新のタイマーをクリア
    const savedTimerId = window.localStorage.getItem(
      REFRESH_ACCESS_TOKEN_TIMER_ID,
    );
    if (savedTimerId) {
      window.clearTimeout(Number(savedTimerId));
    }

    // ログイン状態の場合は4時間ごとにAccessTokenを更新する
    if (isValidRefreshToken && !isLightUserRequest) {
      const timerId = window.setTimeout(async () => {
        await handleRefreshAccessToken({
          cookies,
          async request() {
            return {ok: true, value: null};
          },
        });
      }, 14_400_000);
      window.localStorage.setItem(REFRESH_ACCESS_TOKEN_TIMER_ID, `${timerId}`);
    }
  }

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const {error, resolvedUrl} = pageProps;
  const hasError =
    (error && error.name === InvalidTokenError) || error?.message;

  const ErrorComponent: React.FC = () => {
    if (error && error.name === InvalidTokenError) {
      return <SessionExpiredPage resolvedUrl={resolvedUrl} />;
    }

    if (error?.isStudentVerificationRequired) {
      return <StudentVerificationRequiredPage message={error.message} />;
    }

    if (error?.message) {
      return (
        <ClientSideErrorPage
          message={error.message}
          isShowGssktNextLink={error.isShowGssktNextLink}
        />
      );
    }
  };

  return (
    <>
      <RecoilRoot>
        <ClientProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Head>
                <meta charSet="utf-8" />
                <meta
                  name="viewport"
                  content="width=device-width,initial-scale=1"
                />
                <meta name="format-detection" content="telephone=no" />
                <meta name="mobile-web-app-capable" content="yes" />

                <title key="title">外資就活ドットコム</title>
                <meta
                  key="description"
                  name="description"
                  content="外資系・日系超一流企業を目指す学生のための就職活動サイト。トップ企業の求人情報から、過去の選考情報・先輩のエントリーシート・選考突破法・TOEIC勉強法・インターンシップまで多様なコンテンツを掲載。選考情報一括管理できるカレンダー機能も"
                />
                <meta
                  key="keywords"
                  name="keywords"
                  content="外資,就活,外資系,就職,投資銀行,コンサル,商社"
                />
                <meta key="og:type" property="og:type" content="website" />
                <meta
                  key="og:title"
                  property="og:title"
                  content="外資就活ドットコム"
                />
                <meta
                  key="og:site_name"
                  property="og:site_name"
                  content="外資就活ドットコム"
                />
                <meta
                  key="og:description"
                  property="og:description"
                  content="外資系・日系超一流企業を目指す学生のための就職活動サイト。トップ企業の求人情報から、過去の選考情報・先輩のエントリーシート・選考突破法・TOEIC勉強法・インターンシップまで多様なコンテンツを掲載。選考情報一括管理できるカレンダー機能も有"
                />
                <meta
                  key="og:image"
                  property="og:image"
                  content={`${GSSKT_HOST}${OGP_IMAGE_PATH_DEFAULT}`}
                />
                <meta
                  key="og:url"
                  property="og:url"
                  content={`${GSSKT_HOST}${decodeURI(router.asPath)}`}
                />
                <link
                  rel="canonical"
                  href={`${GSSKT_HOST}${decodeURI(router.asPath)}`}
                  key="canonical"
                />
                <meta key="og:locale" property="og:locale" content="ja_JP" />

                <meta
                  key="fb:app_id"
                  property="fb:app_id"
                  content="152514804805611"
                />
                <meta
                  name="facebook-domain-verification"
                  content="rdf1tzoc35bm3zphygpoqnr9dkghrd"
                />

                <meta
                  key="twitter:card"
                  name="twitter:card"
                  content="summary_large_image"
                />
                <meta
                  key="twitter:site"
                  name="twitter:site"
                  content="@gaishishukatsu"
                />

                <link
                  rel="shortcut icon"
                  type="image/x-icon"
                  href={FAVICON_PATH}
                />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
              </Head>
              {hasError ? (
                <ErrorComponent />
              ) : (
                <SignUpAppealModalProvider>
                  {getLayout(<Component {...pageProps} />)}
                  <RegisterPrompts />
                  <RegistrationNavigationFloating />
                  <SessionExpiredPopup />
                </SignUpAppealModalProvider>
              )}
            </AuthProvider>
          </QueryClientProvider>
        </ClientProvider>
      </RecoilRoot>
      {/* Twitter conversion tracking base code */}
      <Script
        id="twitter-conversion-tracking"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
        !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
        },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
        a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
        twq('config','oeh4c');
        `,
        }}
      />
      {/* End Twitter conversion tracking base code */}
      {/* LINE Tag Base Code  */}

      {/* !-- Do Not Modify */}
      <Script
        id="line-tag-base"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(g,d,o){
            g._ltq=g._ltq||[];g._lt=g._lt||function(){g._ltq.push(arguments)};
            var h=location.protocol==='https:'?'https://d.line-scdn.net':'http://d.line-cdn.net';
            var s=d.createElement('script');s.async=1;
            s.src=o||h+'/n/line_tag/public/release/v1/lt.js';
            var t=d.getElementsByTagName('script')[0];t.parentNode.insertBefore(s,t);
              })(window, document);
          _lt('init', {
            customerType: 'lap',
            tagId: '1010769f-1205-4450-a554-9f3e269d814c'
          });
          _lt('send', 'pv', ['1010769f-1205-4450-a554-9f3e269d814c']);
        `,
        }}
      />
      <noscript>
        <Image
          height="1"
          width="1"
          style={{display: 'none'}}
          src="https://tr.line.me/tag.gif?c_t=lap&t_id=1010769f-1205-4450-a554-9f3e269d814c&e=pv&noscript=1"
          alt="LINE Tag Base Code"
        />
      </noscript>
      {/* End LINE Tag Base Code */}

      {/* Meta Pixel Code（ベースコードは _document で読み込む） */}
      <noscript>
        <Image
          height="1"
          width="1"
          style={{display: 'none'}}
          src="https://www.facebook.com/tr?id=533578025280022&ev=PageView&noscript=1"
          alt="Meta Pixel Code"
        />
      </noscript>
      {/* End Meta Pixel Code */}

      {process.env.NEXT_PUBLIC_RELEASE_STAGE === 'production' && (
        <Script
          id="microsoft-clarity"
          strategy="afterInteractive"
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: ` (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "nr7yywna6p");`,
          }}
        />
      )}
    </>
  );
};

export default App;
